import { z } from "zod";
import { INSTALLED_LANGUAGES } from "../i18n/installed-languages";
import { parseJsonWithSchemaOrDefault } from "../json/parse-json-with-schema";
import { localStorageAdapter, sessionStorageAdapter } from "../storage-utils";

/**
 * Storage key registry
 * Maps storage keys to their Zod schemas for type-safe access
 */
interface StorageKeyMap {
	[key: string]: z.ZodType;
}

/**
 * Typed storage API that validates stored values against Zod schemas
 *
 * This replaces direct localStorage.getItem() calls with validated,
 * type-safe storage access.
 */
export class TypedStorage {
	readonly _keyMap: StorageKeyMap;
	private readonly adapter: typeof localStorageAdapter;

	constructor(keyMap: StorageKeyMap, adapter = localStorageAdapter) {
		this._keyMap = keyMap;
		this.adapter = adapter;
	}

	/**
	 * Get a typed value from storage
	 * @param key - Storage key (must be registered in keyMap)
	 * @param schema - Zod schema to validate the value
	 * @param defaultValue - Optional default value if key doesn't exist or validation fails
	 * @returns The validated and typed value, or null if no default provided
	 */
	getItem<T extends z.ZodType>(
		key: string,
		schema: T,
		defaultValue?: z.infer<T>,
	): z.infer<T> | null {
		const fallback = (defaultValue ?? null) as z.infer<T> | null;
		const raw = this.adapter.getItem(key);
		if (raw === null) {
			return fallback;
		}
		return parseJsonWithSchemaOrDefault(
			raw,
			schema,
			fallback as z.infer<T>,
			`TypedStorage.getItem(${key})`,
		) as z.infer<T>;
	}

	/**
	 * Set a typed value in storage
	 * @param key - Storage key
	 * @param value - Value to store (will be validated against schema if key is registered)
	 * @param schema - Optional schema to validate before storing
	 */
	set<T extends z.ZodType>(key: string, value: unknown, schema?: T): void {
		// Validate if schema is provided
		if (schema) {
			const result = schema.safeParse(value);
			if (!result.success) {
				throw new Error(
					`TypedStorage.set(${key}): Validation failed: ${result.error.issues
						.map((issue) => `${issue.path.join(".")}: ${issue.message}`)
						.join("; ")}`,
					{ cause: result.error },
				);
			}
			this.adapter.setItem(key, JSON.stringify(result.data));
		} else {
			this.adapter.setItem(key, JSON.stringify(value));
		}
	}

	/**
	 * Remove a key from storage
	 */
	remove(key: string): void {
		this.adapter.removeItem(key);
	}

	/**
	 * Clear all storage
	 */
	clear(): void {
		this.adapter.clear();
	}
}

/**
 * Create a typed storage instance with a key map
 */
export function createTypedStorage(keyMap: StorageKeyMap): TypedStorage {
	return new TypedStorage(keyMap);
}

/**
 * Storage version prefix
 * Increment when the persisted storage schema changes.
 */
export const STORAGE_VERSION = "v1" as const;

/**
 * Creates a versioned storage key
 * @param key - The base key name
 * @returns Versioned key in format "key:v1"
 */
export function versionedKey(key: string): string {
	return `${key}:${STORAGE_VERSION}`;
}

/**
 * Storage keys registry
 * All keys are versioned to prevent schema conflicts
 */
export const StorageKeys = {
	// App settings
	locale: versionedKey("locale"),
	theme: versionedKey("theme"),

	// Sidebar state
	sidebarState: versionedKey("sidebar_state"),
	sidebarWidth: versionedKey("sidebar_width"),

	// Destination rail: "1" once the user collapses it to icons; absent or "0"
	// means expanded (icons + names), the default. Persisted because
	// `/dashboard` and the shell routes are separate runtimes, so moving between
	// them remounts the rail — session state alone would forget.
	//
	// A new key rather than `nav:rail-expanded`: that one was written while the
	// rail defaulted to COLLAPSED, and its "0"s would keep those browsers
	// collapsed after the default flipped.
	railCollapsed: versionedKey("nav:rail-collapsed"),

	// Chat
	chatVisibility: versionedKey("chat:visibility"),
	chatDraft: (conversationId: string) =>
		versionedKey(`chat:draft:${conversationId}`),
	// Preferred microphone input device for voice-note recording
	micDeviceId: versionedKey("chat:mic-device-id"),

	// Calendar
	calendarView: versionedKey("calendar:view"),
	calendarDate: versionedKey("calendar:date"),
	calendarPageView: versionedKey("calendar:page:view"),
	calendarPageDate: versionedKey("calendar:page:date"),
	dualLeftCalendarView: versionedKey("dual:left:calendar:view"),
	dualRightCalendarView: versionedKey("dual:right:calendar:view"),

	// AI model favorites
	favoriteProviders: versionedKey("ai-model-favorite-providers"),

	// Settings store (zustand)
	settingsStore: versionedKey("settings-store"),

	// Quick replies store (zustand)
	quickRepliesStore: versionedKey("quick-replies-store"),

	// Emoji picker recents (zustand)
	emojiRecents: versionedKey("emoji-recents-store"),

	// Data grid / documents
	isLocalized: versionedKey("isLocalized"),
} as const;

/**
 * Storage schemas registry
 */
export const StorageSchemas = {
	locale: z.enum(INSTALLED_LANGUAGES),
	theme: z.string(),
} as const;

/**
 * Default typed localStorage instance
 */
export const typedLocalStorage = new TypedStorage({}, localStorageAdapter);

/**
 * Default typed sessionStorage instance
 */
const _typedSessionStorage = new TypedStorage({}, sessionStorageAdapter);
